// components/Toast.jsx
import { useEffect } from "react";
import { Box, Typography, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

const bgBySeverity = (theme, severity) => {
  switch (severity) {
    case "error":
      return theme.palette.error.main;
    case "warning":
      return theme.palette.warning.main;
    case "info":
      return theme.palette.info.main;
    default:
      return theme.palette.success.main;
  }
};

const Toast = ({
  open,
  message,
  duration = 3000,
  onClose,
  severity = "success",
}) => {
  useEffect(() => {
    if (!open) return;
    const id = setTimeout(() => onClose?.(), duration);
    return () => clearTimeout(id);
  }, [open, duration, onClose]);

  if (!open) return null;

  return (
    <Box
      sx={{
        position: "fixed",
        left: "50%",
        top: { xs: "70vh", sm: "55vh" }, // ~bottom 2/3 vertically
        transform: "translateX(-50%)", // center on X
        zIndex: (t) => t.zIndex.snackbar,
        minWidth: { xs: 320, sm: 420 },
        maxWidth: { xs: 480, sm: 560 },
        borderRadius: 2,
        boxShadow: 6,
        bgcolor: (t) => t.palette.background.paper,
        border: (t) => `1px solid ${t.palette.divider}`,
        overflow: "hidden",
      }}
      role="status"
      aria-live="polite"
    >
      {/* Top shrinking bar */}
      <Box
        sx={{
          height: 4,
          bgcolor: (t) => bgBySeverity(t, severity),
          width: "100%",
          "@keyframes toastProgressShrink": {
            from: { width: "100%" },
            to: { width: 0 },
          },
          animation: `${duration}ms linear forwards toastProgressShrink`,
        }}
      />

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          p: { xs: 1.5, sm: 2 },
        }}
      >
        <Typography variant="body1" sx={{ flex: 1 }}>
          {message}
        </Typography>
        <IconButton
          aria-label="Close"
          size="small"
          onClick={() => onClose?.()}
          sx={{ ml: 0.5 }}
        >
          <CloseIcon fontSize="inherit" />
        </IconButton>
      </Box>
    </Box>
  );
};

export default Toast;
